import React, { useEffect, useState } from 'react';
import { PageHeaderLeftIcon } from '@shared/infrastructure/ui/components/header/pageHeader';
import { historyRepository } from '@shared/domain/router/router.repository';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { topicRepository } from '@shared/domain/topic/topic.repository';
import { routeRepository } from '@shared/domain/router/route/route.repository';
import { ROUTES } from '@shared/infrastructure/router/paths';
import { useAppSelector } from '@shared/infrastructure/state/store';
import {
  countryCodeSelector,
  languageCodeSelector
} from '@shared/infrastructure/state/global.slice';
import { Topic } from '@shared/domain/topic/topic.aggregate';
import { TopicEditLayout } from './topicEdit/topicEditLayout';
import { TopicInfo } from './topicEdit/topicInfo';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, Button, IconButton } from '@mui/material';
import { therapyRepository } from '@shared/domain/therapy/therapy.repository';
import { Therapy } from '@shared/domain/therapy/therapy.aggregate';
import { TopicSettings } from './topicEdit/topicSettings';

export const TopicEdit = () => {
  const countryCode = useAppSelector(countryCodeSelector);
  const languageCode = useAppSelector(languageCodeSelector);
  const { topicId } =
    routeRepository.getParams(
      ROUTES.TOPICS_EDIT.path,
      window.location.pathname
    ) || {};

  const [therapies, setTherapies] = useState<Therapy[]>([]);
  useEffect(() => {
    therapyRepository
      .getTherapies(countryCode, languageCode)
      .then((therapies) => {
        setTherapies(therapies || []);
      });
  }, [countryCode, languageCode]);

  const [topic, setTopic] = useState<Topic>();
  useEffect(() => {
    topicRepository.getTopics(countryCode, languageCode).then((topics) => {
      setTopic(topics.find((t) => t.id === Number(topicId)));
      reset(topics.find((t) => t.id === Number(topicId)));
    });
  }, [countryCode, languageCode, topicId]);

  const { control, handleSubmit, reset, watch } = useForm<Topic>();
  const back = () =>
    historyRepository.push(routeRepository.buildRoute(ROUTES.TOPICS.path, {}));
  const onSubmit: SubmitHandler<Topic> = (data) => {
    topicRepository
      .updateTopic(countryCode, languageCode, {
        ...topic,
        ...data
      })
      .then(back, (error) => {
        throw error;
      });
  };

  const therapyId = watch('therapyId')?.toString();

  const [therapy, setTherapy] = useState<Therapy>();
  useEffect(() => {
    therapyRepository
      .getTherapies(countryCode, languageCode)
      .then((therapies) => {
        setTherapy(therapies.find((th) => th.id === Number(therapyId)));
      });
  }, [countryCode, languageCode, therapyId]);

  if (!topic) return null;

  return (
    <>
      <PageHeaderLeftIcon
        icon={
          <IconButton
            size='large'
            color='inherit'
            aria-label='back button'
            sx={{ mr: 2 }}
            onClick={back}
          >
            <ArrowBackIcon fontSize='large' />
          </IconButton>
        }
      >
        <Box sx={{ textAlign: 'center' }}>
          <p>{topic.nameLanguage}</p>
          <p>
            <small>
              Country: {countryCode}, Language: {languageCode}
            </small>
          </p>
        </Box>
      </PageHeaderLeftIcon>
      <Box sx={{ textAlign: 'center' }}>
        <p>{`/${countryCode}/${therapy?.name || 'therapy'}/room/${
          topic?.name
        }`}</p>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TopicEditLayout
          left={
            <TopicInfo
              topic={topic}
              therapyName={therapy?.name}
              control={control}
              disableName
            />
          }
          right={<TopicSettings therapies={therapies} control={control} />}
          buttons={
            <Button
              sx={{
                marginTop: '1rem',
                padding: '1rem 1.5rem'
              }}
              type='submit'
              variant='contained'
              color='primary'
            >
              Save
            </Button>
          }
        ></TopicEditLayout>
      </form>
    </>
  );
};
